import { useEffect, useRef, useState } from 'react';

// Advisory client-side page-count estimate for the letter body, mirroring the
// geometry of the server's real renderer (backend/src/templates/letters/letter.html)
// closely enough to be useful, but NOT byte-exact — the server is authoritative
// (it renders the real PDF via Chromium + pdf-lib page counting, see
// backend/src/jobs/letterPdf.ts). This hook exists only to give the writer a
// live "about how many pages" signal while typing; it is never sent to the
// server and never blocks any action.
//
// Geometry mirrored from letter.html's @page rule:
//   - A4 page: 210mm x 297mm
//   - left/right margins: fixed 20mm each (unconditional)
//   - top margin: 38mm if the org has a header image, else 20mm
//   - bottom margin: 30mm if the org has a footer image/text, else 20mm
//   We don't know the org's letterhead state here (org settings live
//   elsewhere), so we use the WITH-letterhead margins (38mm/30mm) as the
//   default — most orgs configure a letterhead, and slightly under-counting
//   available height (i.e. estimating a few more pages than a bare page
//   would allow) is the safer direction for an advisory count.
//   - body font: 12pt, line-height 1.5, at --fit-scale 1 (the estimate does
//     not attempt to model Task 7's auto-fit shrink search).
const MM_PER_IN = 25.4;
const PX_PER_IN = 96; // CSS px reference pixel
const mmToPx = (mm: number): number => (mm / MM_PER_IN) * PX_PER_IN;

const PAGE_WIDTH_MM = 210;
const PAGE_HEIGHT_MM = 297;
const MARGIN_SIDE_MM = 20;
const MARGIN_TOP_MM = 38;
const MARGIN_BOTTOM_MM = 30;

const BODY_WIDTH_PX = mmToPx(PAGE_WIDTH_MM - 2 * MARGIN_SIDE_MM);
const BODY_HEIGHT_PX = mmToPx(PAGE_HEIGHT_MM - MARGIN_TOP_MM - MARGIN_BOTTOM_MM);

const BODY_FONT_PT = 12;
const BODY_LINE_HEIGHT = 1.5;
const PARAGRAPH_MARGIN_PT = 8; // .letter-body p { margin: 0 0 8pt 0 }

let measureEl: HTMLDivElement | null = null;

// A single shared hidden measuring div, lazily created and reused across
// every call (avoids thrashing the DOM with a fresh off-screen node per
// keystroke). Sized to the printable body box, Faruma body font, RTL —
// matches the template's .letter-body rendering closely enough to be useful.
function getMeasureEl(): HTMLDivElement {
  if (measureEl) return measureEl;
  const el = document.createElement('div');
  el.setAttribute('aria-hidden', 'true');
  el.dir = 'rtl';
  Object.assign(el.style, {
    position: 'absolute',
    visibility: 'hidden',
    pointerEvents: 'none',
    top: '0',
    insetInlineStart: '-99999px',
    width: `${BODY_WIDTH_PX}px`,
    fontFamily: "'Faruma', 'Noto Sans Thaana', sans-serif",
    fontSize: `${BODY_FONT_PT}pt`,
    lineHeight: String(BODY_LINE_HEIGHT),
  } satisfies Partial<CSSStyleDeclaration>);
  document.body.appendChild(el);
  measureEl = el;
  return el;
}

// Renders `bodyHtml` into the hidden measuring div and returns an estimated
// page count (>= 1) given the printable body height. Debounced by the caller
// via the html dependency change (React effect), not internally.
function estimatePages(bodyHtml: string): number {
  if (typeof document === 'undefined') return 1;
  const el = getMeasureEl();
  el.innerHTML = bodyHtml;
  // Force the same paragraph bottom-margin the PDF template uses so wrapped
  // <p> blocks take up comparable vertical space.
  el.querySelectorAll('p').forEach((p) => {
    (p as HTMLElement).style.margin = `0 0 ${PARAGRAPH_MARGIN_PT}pt 0`;
  });
  const height = el.scrollHeight;
  if (height <= 0) return 1;
  return Math.max(1, Math.ceil(height / BODY_HEIGHT_PX));
}

// Live, advisory page-count estimate for `bodyHtml`. Recomputes whenever the
// HTML changes (light debounce so it doesn't thrash on every keystroke).
export function usePageEstimate(bodyHtml: string): number {
  const [pages, setPages] = useState(1);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setPages(estimatePages(bodyHtml));
    }, 200);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [bodyHtml]);

  return pages;
}
